import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import { JobRole } from '../../mocks/companyTypes';
import { useAuth } from '../../contexts/AuthContext';
import { usePlayMode } from '../../hooks/usePlayMode';

export default function RoleSelectPage() {
  const navigate = useNavigate();
  const { setJobRole, roleConfigs, companyConfig } = useRole();
  const { profile } = useAuth();
  const { isPlugAndPlay } = usePlayMode();
  const [selected, setSelected] = useState<JobRole | null>(null);
  const [hovering, setHovering] = useState<JobRole | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Mode B (Plug & Play)에서는 CEO는 AI가 담당 → 선택 불가
  const availableRoles = isPlugAndPlay
    ? roleConfigs.filter(r => r.key !== "CEO")
    : roleConfigs;

  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    await setJobRole(selected);
    setShowConfirmModal(false);
    navigate('/dashboard');
  };

  const selectedConfig = roleConfigs.find(r => r.key === selected);

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex flex-col items-center justify-center px-8 py-12">
      {/* Header */}
      <div className="text-center mb-10 max-w-lg">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <img
            src="https://storage.readdy-site.link/project_files/e81f0f74-7f28-46c8-8485-3df778381a50/9a17726c-ea2b-4ef1-b154-44e1a8dd8821_Gemini_Generated_Image_at6qf4at6qf4at6q-removebg-preview.png?v=7e9fc5a9989fd2f2c3e90b758ae037ba"
            alt="K-ArtPEN"
            className="w-8 h-8 rounded-lg object-contain opacity-80"
          />
          <span className="text-[#F2F2F2] font-semibold text-sm tracking-widest">K-ArtPEN</span>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4B6CF7]/10 border border-[#4B6CF7]/20 mb-4">
          <i className={`${companyConfig.icon} text-[#7A9BFF] text-xs`}></i>
          <span className="text-[#7A9BFF] text-xs font-medium">{companyConfig.label}</span>
        </div>
        <h1 className="text-[#F2F2F2] text-2xl font-semibold tracking-tight mb-3">
          {profile?.display_name ? `${profile.display_name}님, ` : ''}어떤 직무를 맡으시겠습니까?
        </h1>
        <p className="text-[#666666] text-sm leading-relaxed">
          선택한 직무에 따라 보이는 화면과 업무가 달라집니다.
        </p>

        {/* ⚠️ 잠금 경고 배너 */}
        <div className="flex items-center gap-2.5 mt-4 px-4 py-3 rounded-xl bg-amber-500/[0.08] border border-amber-500/25">
          <i className="ri-lock-line text-amber-400/80 text-sm shrink-0"></i>
          <p className="text-amber-400/80 text-xs leading-relaxed text-left">
            <strong>직무는 한 번 선택하면 변경할 수 없습니다.</strong><br />
            변경이 필요할 경우 반드시 관리자(강사)에게 문의하세요.
          </p>
        </div>

        {/* MODE B: AI CEO 안내 */}
        {isPlugAndPlay && (
          <div className="flex items-center gap-2.5 mt-3 px-4 py-3 rounded-xl bg-[#4B6CF7]/[0.08] border border-[#4B6CF7]/25">
            <i className="ri-robot-2-line text-[#7A9BFF] text-sm shrink-0"></i>
            <p className="text-[#7A9BFF]/80 text-xs leading-relaxed text-left">
              <strong>Plug &amp; Play 모드:</strong> CEO는 AI(김준혁 대표)가 담당합니다. 여러분은 실무 직무를 선택하세요. AI CEO로부터 업무 지시와 피드백을 받게 됩니다.
            </p>
          </div>
        )}
      </div>

      {/* Role Cards */}
      <div className={`grid gap-3 w-full max-w-4xl mb-8 grid-cols-2 md:grid-cols-3 lg:grid-cols-${availableRoles.length + (isPlugAndPlay ? 1 : 0)}`}>
        {/* MODE B: AI CEO 고정 카드 */}
        {isPlugAndPlay && (
          <div className="relative text-left rounded-xl border border-[#4B6CF7]/25 bg-[#4B6CF7]/[0.04] p-0 overflow-hidden opacity-80">
            <div className="p-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg mb-4 bg-[#4B6CF7]/10 border border-[#4B6CF7]/15">
                <i className="ri-robot-2-line text-lg text-[#7A9BFF]"></i>
              </div>
              <div className="text-sm font-semibold mb-1 leading-tight text-[#7A9BFF]">
                대표이사 (AI)
              </div>
              <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-3 leading-tight">
                CEO — AI Managed
              </div>
              <p className="text-xs leading-relaxed mb-4 text-[#555555]">
                AI 대표이사 '김준혁'이 CEO 역할을 맡습니다. 업무 지시, 결재 검토, 이벤트 대응 평가를 자동으로 수행합니다.
              </p>
              <div className="space-y-1.5">
                {["직원 업무 지시 및 피드백", "결재 서류 검토·승인", "이벤트 대응 평가"].map(t => (
                  <div key={t} className="flex items-center gap-1.5">
                    <div className="w-1 h-1 rounded-full shrink-0 bg-[#4B6CF7]/40" />
                    <span className="text-[10px] leading-tight text-[#444444]">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full bg-[#4B6CF7]/20 border border-[#4B6CF7]/30">
              <i className="ri-robot-line text-[#7A9BFF] text-xs"></i>
            </div>
          </div>
        )}

        {availableRoles.map((role) => {
          const isSelected = selected === role.key;
          const isHovering = hovering === role.key;
          return (
            <button
              key={role.key}
              type="button"
              onClick={() => setSelected(role.key)}
              onMouseEnter={() => setHovering(role.key)}
              onMouseLeave={() => setHovering(null)}
              className={`relative text-left rounded-xl border transition-all duration-200 cursor-pointer p-0 overflow-hidden ${
                isSelected
                  ? 'border-[#4B6CF7]/40 bg-[#4B6CF7]/[0.07]'
                  : isHovering
                  ? 'border-white/10 bg-white/[0.03]'
                  : 'border-white/[0.06] bg-[#111112]'
              }`}
            >
              <div className="p-5">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-4 transition-colors ${
                  isSelected ? 'bg-[#4B6CF7]/15 border border-[#4B6CF7]/20' : 'bg-white/[0.04]'
                }`}>
                  <i className={`${role.icon} text-lg ${isSelected ? 'text-[#7A9BFF]' : 'text-[#666666]'}`}></i>
                </div>
                <div className={`text-sm font-semibold mb-1 leading-tight transition-colors ${isSelected ? 'text-[#F2F2F2]' : 'text-[#999999]'}`}>
                  {role.label.split('/')[0].trim()}
                </div>
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-3 leading-tight">
                  {role.labelEn.split('&')[0].trim()}
                </div>
                <p className={`text-xs leading-relaxed mb-4 transition-colors ${isSelected ? 'text-[#888888]' : 'text-[#444444]'}`}>
                  {role.description}
                </p>
                <div className="space-y-1.5">
                  {role.tasks.slice(0, 3).map((task) => (
                    <div key={task} className="flex items-center gap-1.5">
                      <div className={`w-1 h-1 rounded-full shrink-0 ${isSelected ? 'bg-[#4B6CF7]/60' : 'bg-white/10'}`} />
                      <span className={`text-[10px] leading-tight ${isSelected ? 'text-[#666666]' : 'text-[#333333]'}`}>{task}</span>
                    </div>
                  ))}
                </div>
              </div>
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full bg-[#4B6CF7]/20 border border-[#4B6CF7]/30">
                  <i className="ri-check-line text-[#7A9BFF] text-xs"></i>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected detail */}
      {selectedConfig && (
        <div className="w-full max-w-4xl mb-6 bg-[#111112] border border-white/[0.06] rounded-xl p-5 flex items-center gap-6">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/[0.06] shrink-0">
            <i className={`${selectedConfig.icon} text-lg text-[#F2F2F2]`}></i>
          </div>
          <div className="flex-1">
            <div className="text-[#F2F2F2] text-sm font-semibold mb-1">
              {selectedConfig.label} 으로 시작합니다
            </div>
            <div className="text-[#555555] text-xs">
              접근 가능한 메뉴: {selectedConfig.mainNav.join(' · ')}
            </div>
          </div>
          <div className="text-[#333333] text-xs text-right">
            동료 부서와 <br />유기적으로 연결됩니다
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        type="button"
        onClick={() => selected && setShowConfirmModal(true)}
        disabled={!selected || confirming}
        className={`px-10 py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
          selected
            ? 'bg-[#4B6CF7] text-white hover:bg-[#5B7CF7] cursor-pointer'
            : 'bg-white/[0.04] text-[#333333] cursor-not-allowed'
        }`}
      >
        {confirming ? '입장 중...' : selected ? `${selectedConfig?.label} 으로 입장` : '직무를 선택해주세요'}
      </button>

      <button
        type="button"
        onClick={() => navigate('/company-select')}
        className="mt-4 text-[#333333] text-xs hover:text-[#555555] transition-colors cursor-pointer whitespace-nowrap"
      >
        ← 기업 유형 다시 선택
      </button>

      {/* ── 최종 확인 모달 ── */}
      {showConfirmModal && selectedConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !confirming && setShowConfirmModal(false)} />
          <div className="relative w-full max-w-sm mx-4 bg-[#111112] border border-white/[0.1] rounded-2xl overflow-hidden shadow-2xl animate-[fadeIn_0.25s_ease]">
            {/* Warning strip */}
            <div className="bg-amber-500/10 border-b border-amber-500/20 px-5 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-amber-400/80 text-base"></i>
              <span className="text-amber-400/80 text-xs font-semibold">신중하게 확인해주세요</span>
            </div>

            <div className="px-6 py-6">
              {/* Selected role display */}
              <div className="flex items-center gap-3 mb-5 p-4 bg-[#4B6CF7]/[0.07] border border-[#4B6CF7]/20 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4B6CF7]/15 shrink-0">
                  <i className={`${selectedConfig.icon} text-[#7A9BFF] text-lg`}></i>
                </div>
                <div>
                  <div className="text-[#F2F2F2] font-semibold text-sm">{selectedConfig.label}</div>
                  <div className="text-[#7A9BFF]/70 text-[10px]">{selectedConfig.labelEn}</div>
                </div>
              </div>

              <h3 className="text-[#F2F2F2] font-semibold text-sm mb-2">이 직무로 확정하시겠습니까?</h3>
              <p className="text-[#666666] text-xs leading-relaxed mb-2">
                직무를 확정하면 <strong className="text-amber-400/80">이후 직접 변경이 불가능합니다.</strong>
              </p>
              <p className="text-[#444444] text-xs leading-relaxed mb-6">
                변경이 필요한 경우 반드시 관리자(강사)에게 요청하세요. 잘못 선택한 경우를 대비해 반드시 위의 직무명을 다시 한 번 확인해주세요.
              </p>

              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={confirming}
                  className="flex-1 py-2.5 rounded-xl text-[#555555] border border-white/[0.08] hover:border-white/[0.2] transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  다시 선택
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="flex-1 py-2.5 rounded-xl bg-[#4B6CF7] text-white hover:bg-[#5B7CF7] disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap text-sm font-semibold flex items-center justify-center gap-1.5"
                >
                  {confirming ? (
                    <><div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin"></div>입장 중...</>
                  ) : (
                    <><i className="ri-lock-line text-sm"></i>확정하고 입장</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
